/**
 * The architecture around the plot — a fragment of earthen building set on
 * the player's own floor.
 *
 * This ships in **all three AR modes**, unlike the horizon, because it is
 * small enough to sit on a table without pretending to replace the room. A
 * ring of building around the beds is what turns a planter into a *place*.
 *
 * ## What it quotes
 *
 * The vocabulary is mud-brick: thick tapered earthen walls, triangular
 * merlons stepping along every parapet, pointed arcades. It reads as old,
 * hand-built and hot-climate without being any one tradition's building —
 * though it began as a quotation of one specific regional style, and giving
 * it a form that belongs anywhere is outstanding work (see the rebrand spec
 * in `docs/superpowers/specs/`). The dome is a geodesic glasshouse, the one
 * building here the desert rose would actually live in.
 *
 * ## Why it is a broken ring
 *
 * A closed wall reads as a prison and, more practically, hides the garden
 * from the low camera angles `preview` allows. Three arcs with gaps between
 * them frame the plot without enclosing it, and the gaps are where the player
 * looks in.
 *
 * ## Detail lives in the silhouette
 *
 * At 0.86 m across, viewed from two and a half metres, a merlon is a couple
 * of pixels. Everything here is shaped so it reads as an outline: crenellated
 * tops, arch voids punched right through, a faceted dome. Surface ornament at
 * this scale is triangles spent on nothing.
 */

import * as THREE from 'three';

export interface Pavilion {
  root: THREE.Group;
  /** Plays the spawn animation. Call once after placing. */
  reveal(): void;
  update(dt: number, time: number): void;
  /** 0 = broad daylight, 1 = lanterns carrying the scene. */
  setNight(amount: number): void;
  dispose(): void;
}

const EARTH = 0xc4a882;
const EARTH_DARK = 0xa88a66;
const STONE = 0xd8cbb2;
const LANTERN = 0xffb457;

/**
 * A flat triangular merlon — the row of teeth along every earthen parapet, and
 * the single most recognisable thing about the style from a distance.
 */
function merlonGeometry(halfWidth: number, height: number, depth: number) {
  const shape = new THREE.Shape();
  shape.moveTo(-halfWidth, 0);
  shape.lineTo(halfWidth, 0);
  shape.lineTo(0, height);
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false });
  geo.translate(0, 0, -depth / 2);
  return geo;
}

/**
 * A wall panel with a pointed arch cut clean through it.
 *
 * The apex is a genuine cusp rather than a semicircle: each side arrives on a
 * steep tangent, which is what separates an Islamic pointed arch from a Roman
 * round one. Control points sit low for that reason — pulled up toward the
 * apex they round it off and the whole thing reads Roman.
 */
function archPanelGeometry(width: number, height: number, depth: number) {
  const shape = new THREE.Shape();
  shape.moveTo(-width / 2, 0);
  shape.lineTo(width / 2, 0);
  shape.lineTo(width / 2, height);
  shape.lineTo(-width / 2, height);
  shape.closePath();

  const half = width * 0.3;
  const spring = height * 0.4;
  const apex = height * 0.88;

  const hole = new THREE.Path();
  hole.moveTo(-half, 0);
  hole.lineTo(-half, spring);
  hole.quadraticCurveTo(-half * 0.8, apex * 0.52, 0, apex);
  hole.quadraticCurveTo(half * 0.8, apex * 0.52, half, spring);
  hole.lineTo(half, 0);
  hole.closePath();
  shape.holes.push(hole);

  const geo = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false });
  geo.translate(0, 0, -depth / 2);
  return geo;
}

export function buildPavilion(radius = 0.86): Pavilion {
  const root = new THREE.Group();
  const owned: Array<{ dispose(): void }> = [];
  const track = <T extends { dispose(): void }>(x: T): T => {
    owned.push(x);
    return x;
  };

  const earthMat = track(
    new THREE.MeshStandardMaterial({ color: EARTH, roughness: 1, flatShading: true }),
  );
  const earthDarkMat = track(
    new THREE.MeshStandardMaterial({ color: EARTH_DARK, roughness: 1, flatShading: true }),
  );
  const stoneMat = track(
    new THREE.MeshStandardMaterial({ color: STONE, roughness: 0.92, flatShading: true }),
  );

  /*
   * Lowered from 0.15. At that height the wall stood taller than the plot's
   * whole terrace from a normal viewing angle and cut the beds in half — it
   * enclosed the garden instead of framing it. A parapet you look over beats
   * one you look at.
   */
  /*
   * Everything below is authored against the 0.86 m ring this started as and
   * then scaled. The plot opens out to more than twice that as districts
   * unlock, and absolute constants left a 10 cm parapet standing around a
   * 3.2 m garden — a knee-high fence rather than architecture.
   */
  const s = radius / 0.86;

  const WALL_H = 0.105 * s;
  const WALL_T = 0.034 * s;
  /*
   * Merlon proportions matter more here than anything else, and the first
   * pass got them badly wrong: at 0.042 tall and 0.038 wide on a 0.15 wall
   * they were a third of the wall's height and nearly touching, so the whole
   * ring read as a garden picket fence. An earthen parapet is a row of small,
   * clearly separated teeth sitting on a visibly solid mass of wall.
   */
  const MERLON_H = 0.022 * s;
  const MERLON_W = 0.0085 * s;
  const MERLON_PITCH = 0.028 * s;

  /* ---------------------------------------------------------------- *
   * Crenellated wall arcs
   *
   * Wall, plinth and merlons are three InstancedMeshes. Forty separate
   * meshes would be forty draw calls for something that is background by
   * definition, on a device that also has a camera feed, twelve procedural
   * plants and a particle system to get through every frame.
   * ---------------------------------------------------------------- */

  const boxGeo = track(new THREE.BoxGeometry(1, 1, 1));
  const merlonGeo = track(merlonGeometry(MERLON_W, MERLON_H, WALL_T * 0.8));

  // Three arcs, leaving gaps to look through. Angles in radians.
  const ARCS: { from: number; to: number }[] = [
    { from: 0.32, to: 2.05 },
    { from: 2.62, to: 3.9 },
    { from: 4.5, to: 5.75 },
  ];

  const segMatrices: THREE.Matrix4[] = [];
  const plinthMatrices: THREE.Matrix4[] = [];
  const merlonMatrices: THREE.Matrix4[] = [];
  const q = new THREE.Quaternion();
  const euler = new THREE.Euler();
  const pos = new THREE.Vector3();
  const scale = new THREE.Vector3();

  const compose = (
    a: number,
    y: number,
    sx: number,
    sy: number,
    sz: number,
    yaw = -a,
  ): THREE.Matrix4 => {
    euler.set(0, yaw, 0);
    q.setFromEuler(euler);
    pos.set(Math.cos(a) * radius, y, Math.sin(a) * radius);
    scale.set(sx, sy, sz);
    return new THREE.Matrix4().compose(pos, q, scale);
  };

  for (const arc of ARCS) {
    const span = arc.to - arc.from;
    const segments = Math.max(3, Math.round((span * radius) / (0.05 * s)));
    for (let i = 0; i < segments; i++) {
      const a = arc.from + (span * (i + 0.5)) / segments;
      // Chord slightly over-long so neighbours overlap and the wall has no
      // visible gaps where the facets meet.
      const chord = (span * radius) / segments + 0.006 * s;
      /*
       * `-a + PI/2` puts local X along the tangent. Leaving it at `-a` — the
       * obvious-looking value — points every segment *radially*, so instead
       * of a wall you get thirty little planks sticking out of a circle like
       * a picket fence. It looked so much like a deliberate fence that the
       * merlons on top were what got blamed first.
       */
      const yaw = -a + Math.PI / 2;
      segMatrices.push(compose(a, WALL_H / 2, chord, WALL_H, WALL_T, yaw));
      // A wider course along the base. Mud-brick walls batter outward at the
      // foot, and without it the wall reads as a sheet of card stood on edge.
      plinthMatrices.push(
        compose(a, WALL_H * 0.11, chord, WALL_H * 0.22, WALL_T * 1.5, yaw),
      );
    }

    const merlons = Math.max(3, Math.round((span * radius) / MERLON_PITCH));
    for (let i = 0; i < merlons; i++) {
      const a = arc.from + (span * (i + 0.5)) / merlons;
      merlonMatrices.push(compose(a, WALL_H, 1, 1, 1, -a + Math.PI / 2));
    }
  }

  /* ---------------------------------------------------------------- *
   * Gateway — a taller arch in the widest gap
   * ---------------------------------------------------------------- */

  const GATE_W = 0.2 * s;
  const GATE_H = 0.28 * s;
  const gateAngle = 2.33;
  const gate = new THREE.Mesh(
    track(archPanelGeometry(GATE_W, GATE_H, WALL_T * 1.3)),
    earthMat,
  );
  gate.position.set(Math.cos(gateAngle) * radius, 0, Math.sin(gateAngle) * radius);
  gate.rotation.y = -gateAngle + Math.PI / 2;
  gate.castShadow = true;
  gate.receiveShadow = true;
  root.add(gate);

  // Crown the gateway from the same instance buffer as the walls, so it
  // belongs to the parapet instead of reading as a separate object.
  for (let i = -3; i <= 3; i++) {
    const off = i * MERLON_PITCH;
    euler.set(0, -gateAngle + Math.PI / 2, 0);
    q.setFromEuler(euler);
    pos.set(
      Math.cos(gateAngle) * radius - Math.sin(gateAngle) * off,
      GATE_H,
      Math.sin(gateAngle) * radius + Math.cos(gateAngle) * off,
    );
    merlonMatrices.push(new THREE.Matrix4().compose(pos, q, scale.set(1.1, 1.1, 1.1)));
  }

  const walls = new THREE.InstancedMesh(boxGeo, earthMat, segMatrices.length);
  segMatrices.forEach((mat, i) => walls.setMatrixAt(i, mat));
  walls.instanceMatrix.needsUpdate = true;
  walls.castShadow = true;
  walls.receiveShadow = true;
  root.add(walls);

  const plinths = new THREE.InstancedMesh(boxGeo, earthDarkMat, plinthMatrices.length);
  plinthMatrices.forEach((mat, i) => plinths.setMatrixAt(i, mat));
  plinths.instanceMatrix.needsUpdate = true;
  plinths.castShadow = true;
  plinths.receiveShadow = true;
  root.add(plinths);

  const merlonMesh = new THREE.InstancedMesh(merlonGeo, earthMat, merlonMatrices.length);
  merlonMatrices.forEach((mat, i) => merlonMesh.setMatrixAt(i, mat));
  merlonMesh.instanceMatrix.needsUpdate = true;
  merlonMesh.castShadow = true;
  root.add(merlonMesh);

  /* ---------------------------------------------------------------- *
   * Riwaq — a short arcade in the second gap
   * ---------------------------------------------------------------- */

  const arcadeGeo = track(archPanelGeometry(0.115 * s, 0.2 * s, WALL_T));
  const arcade = new THREE.InstancedMesh(arcadeGeo, stoneMat, 3);
  for (let i = 0; i < 3; i++) {
    const a = 4.02 + i * 0.135;
    arcade.setMatrixAt(i, compose(a, 0, 1, 1, 1, -a + Math.PI / 2));
  }
  arcade.instanceMatrix.needsUpdate = true;
  arcade.castShadow = true;
  arcade.receiveShadow = true;
  root.add(arcade);

  /* ---------------------------------------------------------------- *
   * Geodesic conservatory — the one building the desert rose lives in
   * ---------------------------------------------------------------- */

  const domeAngle = 5.95;
  const domeR = 0.125 * s;
  const dome = new THREE.Group();
  dome.position.set(Math.cos(domeAngle) * radius, 0, Math.sin(domeAngle) * radius);
  root.add(dome);

  const domeGeo = track(
    new THREE.SphereGeometry(domeR, 9, 5, 0, Math.PI * 2, 0, Math.PI / 2),
  );
  const glassMat = track(
    new THREE.MeshStandardMaterial({
      color: 0xbfe4dd,
      roughness: 0.25,
      metalness: 0.05,
      transparent: true,
      opacity: 0.42,
      flatShading: true,
      side: THREE.DoubleSide,
    }),
  );
  const domeShell = new THREE.Mesh(domeGeo, glassMat);
  domeShell.position.y = 0.022 * s;
  dome.add(domeShell);

  // The struts are the whole read on a geodesic — without them a faceted
  // hemisphere is just a blob.
  const struts = new THREE.LineSegments(
    track(new THREE.EdgesGeometry(domeGeo, 1)),
    track(new THREE.LineBasicMaterial({ color: 0x8f7f66 })),
  );
  struts.position.y = 0.022 * s;
  dome.add(struts);

  const plinth = new THREE.Mesh(
    track(new THREE.CylinderGeometry(domeR * 1.06, domeR * 1.12, 0.022 * s, 12)),
    stoneMat,
  );
  plinth.position.y = 0.011 * s;
  plinth.receiveShadow = true;
  plinth.castShadow = true;
  dome.add(plinth);

  /* ---------------------------------------------------------------- *
   * Lanterns
   *
   * One instanced mesh for the posts and one for the flames. The flames use
   * a basic material with a per-instance colour: they are light sources in
   * the fiction, so they should not themselves be lit, and per-instance
   * colour is the only way to let six of them flicker out of step. A single
   * shared emissive material made all six pulse in lockstep, which reads as
   * a global brightness bug rather than as fire.
   * ---------------------------------------------------------------- */

  const LANTERN_ANGLES = [0.32, 2.05, 2.62, 3.9, 4.5, 5.75];
  const POST_H = WALL_H + 0.05 * s;

  const posts = new THREE.InstancedMesh(
    track(new THREE.CylinderGeometry(0.0045 * s, 0.007 * s, POST_H, 5)),
    earthDarkMat,
    LANTERN_ANGLES.length,
  );
  LANTERN_ANGLES.forEach((a, i) =>
    posts.setMatrixAt(i, compose(a, POST_H / 2, 1, 1, 1, -a + Math.PI / 2)),
  );
  posts.instanceMatrix.needsUpdate = true;
  posts.castShadow = true;
  root.add(posts);

  const flames = new THREE.InstancedMesh(
    track(new THREE.IcosahedronGeometry(0.013 * s, 0)),
    track(new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false })),
    LANTERN_ANGLES.length,
  );
  flames.instanceColor = new THREE.InstancedBufferAttribute(
    new Float32Array(LANTERN_ANGLES.length * 3),
    3,
  );
  flames.frustumCulled = false;
  root.add(flames);

  const flameBase = new THREE.Color(LANTERN);
  const flameMatrices = LANTERN_ANGLES.map((a) => compose(a, POST_H + 0.014 * s, 1, 1, 1));

  /* ---------------------------------------------------------------- *
   * Lifecycle
   * ---------------------------------------------------------------- */

  let revealT = 1;
  let night = 0;
  const tmpMatrix = new THREE.Matrix4();
  const tmpScale = new THREE.Vector3();

  return {
    root,
    reveal() {
      revealT = 0;
    },
    setNight(amount: number) {
      night = THREE.MathUtils.clamp(amount, 0, 1);
    },
    update(dt, time) {
      if (revealT < 1) {
        // Rises a beat behind the plot, so the ground arrives first and the
        // building settles onto it.
        revealT = Math.min(1, revealT + dt * 1.25);
        const e = 1 - Math.pow(1 - revealT, 3);
        root.scale.set(1, Math.max(0.001, e), 1);
        root.position.y = (1 - e) * -0.05 * s;
      }

      const color = flames.instanceColor;
      if (!color) return;
      for (let i = 0; i < LANTERN_ANGLES.length; i++) {
        // Two detuned sines plus a fast tremor. Detuned so the pattern never
        // repeats on a beat the eye can pick up, which is what separates a
        // flame from a blinking LED.
        const t = time * 1.6 + i * 1.7;
        const flame =
          0.72 +
          Math.sin(t) * 0.12 +
          Math.sin(t * 2.37 + 1.1) * 0.09 +
          Math.sin(t * 7.1) * 0.05;
        const v = (0.45 + night * 0.75) * flame;
        color.setXYZ(i, flameBase.r * v, flameBase.g * v, flameBase.b * v);

        const s = 0.75 + flame * 0.2 + night * 0.4;
        tmpMatrix.copy(flameMatrices[i]);
        tmpMatrix.scale(tmpScale.set(s, s, s));
        flames.setMatrixAt(i, tmpMatrix);
      }
      color.needsUpdate = true;
      flames.instanceMatrix.needsUpdate = true;
    },
    dispose() {
      for (const o of owned) o.dispose();
      owned.length = 0;
    },
  };
}
